import { useApolloClient } from "@apollo/client";
import { isNil } from "lodash";
import { useEffect, useState } from "react";
import {
  fetchPlanGeneralCosts,
  fetchPlanPrice,
} from "utils/helpers/planPrices";

export const plansToPrice = (insurancePlans, selectedIds, priceUnselected) => {
  const plans = insurancePlans || [];
  if (selectedIds && selectedIds.length) {
    return plans.filter((plan) => selectedIds.includes(plan.id));
  }
  if (!priceUnselected) return [];
  return plans;
};

export const formattedPlanPrices = (generalCosts) =>
  Object.keys(generalCosts || {}).reduce((prices, planId) => {
    const formatted = generalCosts[planId] && generalCosts[planId].formatted;
    if (formatted) prices[planId] = formatted;
    return prices;
  }, {});

const costsFromGeneralCosts = (generalCosts) =>
  Object.keys(generalCosts || {}).reduce((costs, planId) => {
    const entry = generalCosts[planId];
    if (!entry || !entry.formatted) return costs;
    costs[planId] = {
      cost: entry.cost,
      currency: entry.currency,
      costDetails: entry.costDetails || null,
    };
    return costs;
  }, {});

// The stored general cost wins so the card shows what the subscription reads.
// A plan without one keeps the insurancePlanPrice compute.
export const usePlanPrices = (
  project,
  insurancePlans,
  { priceUnselected = false, selectedIds = [], screenVisit = 0 } = {},
) => {
  const client = useApolloClient();
  const [planPrices, setPlanPrices] = useState({});
  const [planCosts, setPlanCosts] = useState({});
  const [planScores, setPlanScores] = useState({});
  const projectId = project && project.id;
  const selectedKey = selectedIds.join(",");
  const planKey = (insurancePlans || []).map((plan) => plan.id).join(",");

  useEffect(() => {
    const plans = plansToPrice(insurancePlans, selectedIds, priceUnselected);
    if (!projectId || !plans.length) return undefined;
    let cancelled = false;
    const fetchPrices = async () => {
      const generalCosts = await fetchPlanGeneralCosts(
        client,
        projectId,
        plans.map((plan) => plan.id),
      );
      const prices = formattedPlanPrices(generalCosts);
      const costs = costsFromGeneralCosts(generalCosts);
      const scores = {};
      await Promise.all(
        plans.map(async (plan) => {
          const computeField = await fetchPlanPrice(client, projectId, plan.id);
          if (!prices[plan.id] && computeField?.formatted) {
            prices[plan.id] = computeField.formatted;
            costs[plan.id] = {
              cost: computeField.cost,
              currency: computeField.currency,
              costDetails: computeField.costDetails || null,
            };
          }
          if (!isNil(computeField?.recommendationScore))
            scores[plan.id] = computeField.recommendationScore;
        }),
      );
      if (cancelled) return;
      setPlanScores(scores);
      setPlanPrices(prices);
      setPlanCosts(costs);
    };
    fetchPrices();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, priceUnselected, projectId, selectedKey, screenVisit, client]);

  return { planPrices, planCosts, planScores };
};
